import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Zap, Flame, Check, ArrowRight } from 'lucide-react';
import StromGasFormular from '../components/StromGasFormular';

const StromGasPage = () => {
  return (
    <div className="pt-24 pb-16">
      <Helmet>
        <title>Strom & Gas Tarife Saarland vergleichen - Günstig wechseln | Easy Vertrag Völklingen</title>
        <meta name="description" content="Strom und Gas im Saarland günstiger machen – Tarife vergleichen mit Easy Vertrag in Völklingen. Persönliche Beratung, kompletter Wechselservice. Bis zu 800 € sparen!" />
        <meta name="keywords" content="Strom Gas Saarland, Strom wechseln Völklingen, Energieanbieter Saarland, günstiger Strom Saar, Gastarif Völklingen, Energiekosten senken" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://easy4vertrag.de/strom-gas" />
        <meta property="og:title" content="Strom & Gas Tarife vergleichen | Easy Vertrag" />
        <meta property="og:description" content="Vergleichen Sie hunderte Strom- und Gastarife. Wir übernehmen den kompletten Wechselprozess für Sie." />
        <meta property="og:url" content="https://easy4vertrag.de/strom-gas" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://easy4vertrag.de/"},
              {"@type": "ListItem", "position": 2, "name": "Strom & Gas", "item": "https://easy4vertrag.de/strom-gas"}
            ]
          }
        `}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-600 via-blue-600 to-green-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="flex space-x-4">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <Zap className="w-8 h-8" />
                </div>
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <Flame className="w-8 h-8" />
                </div>
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-4">Strom & Gas</h1>
            <p className="text-xl text-blue-100">
              Finden Sie die besten Tarife für Strom und Gas - einfach, schnell und transparent
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <StromGasFormular />
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
              Warum Strom & Gas bei Easy Vertrag?
            </h2>
            <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
              Der Energiemarkt bietet viele Möglichkeiten zu sparen. Durch einen Anbieterwechsel können Haushalte oft mehrere Hundert Euro pro Jahr sparen - ohne Qualitätsverluste.
            </p>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center bg-white p-6 rounded-xl shadow-sm">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Beste Preise</h3>
                <p className="text-gray-600">
                  Wir vergleichen hunderte Tarife und finden für Sie die günstigsten Angebote aller deutschen Energieanbieter.
                </p>
              </div>

              <div className="text-center bg-white p-6 rounded-xl shadow-sm">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Schneller Wechsel</h3>
                <p className="text-gray-600">
                  Wir übernehmen den kompletten Wechselprozess für Sie - einfach und stressfrei innerhalb weniger Tage.
                </p>
              </div>

              <div className="text-center bg-white p-6 rounded-xl shadow-sm">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Flame className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Persönliche Beratung</h3>
                <p className="text-gray-600">
                  Unser Expertenteam berät Sie individuell und findet den perfekten Tarif für Ihren Haushalt.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Savings Explainer */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">So viel können Sie beim Strom & Gas sparen</h2>
            <p className="text-gray-600 mb-6">
              Der durchschnittliche 3-Personen-Haushalt in Deutschland zahlt ca. 1.500 € pro Jahr für Strom. Durch einen Anbieterwechsel lässt sich dieser Betrag oft um 200-400 € reduzieren. Bei Gas sind ähnliche Einsparungen möglich.
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                'Keine Serviceunterbrechung beim Wechsel',
                'Kündigung beim alten Anbieter übernehmen wir',
                'Alle Ökostrom-Tarife im Vergleich',
                'Tarife mit Preisgarantie verfügbar',
                'Bonus-Zahlungen bei Neuabschluss',
                'Transparente Preise ohne versteckte Kosten',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Häufige Fragen zu Strom & Gas</h2>
            <div className="space-y-3">
              {[
                {
                  q: 'Wie oft sollte ich meinen Stromanbieter wechseln?',
                  a: 'Experten empfehlen, alle 1-2 Jahre zu vergleichen. Nach dem Ende der Mindestlaufzeit (meist 12 Monate) können Sie ohne Zusatzkosten kündigen und zum günstigsten Anbieter wechseln.',
                },
                {
                  q: 'Gibt es eine Unterbrechung der Stromversorgung beim Wechsel?',
                  a: 'Nein! Ein Anbieterwechsel läuft vollständig im Hintergrund ab. Ihre Stromversorgung wird nicht unterbrochen. Der neue Anbieter koordiniert alles mit dem Netzbetreiber.',
                },
                {
                  q: 'Was ist Ökostrom und lohnt er sich?',
                  a: 'Ökostrom stammt aus erneuerbaren Energiequellen wie Wind, Solar und Wasser. Der Preisunterschied zu normalem Strom ist heute oft gering (1-3 Cent/kWh). Bei Easy Vertrag finden Sie alle Ökostrom-Tarife im Vergleich.',
                },
              ].map((item, i) => (
                <details key={i} className="bg-white rounded-lg shadow-sm group">
                  <summary className="font-semibold text-gray-800 cursor-pointer list-none flex justify-between items-center p-5">
                    {item.q}
                    <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="px-5 pb-5 text-gray-600">{item.a}</p>
                </details>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Link to="/kontakt" className="inline-flex items-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors">
                Jetzt Beratung anfragen <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StromGasPage;